import { mergeSavedCards } from './guestProgress';

export const STORY_SAVE_KEY = 'mns-story-saves-v1';

export interface SavedCard {
  id: string;
  pathSlug: string;
  lessonSlug: string;
  index: number;
  title: string;
  lines: { main: string; sub: string }[];
  summary: boolean;
  savedAt: string;
}

function storage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

export function normalizeSaves(raw: unknown): SavedCard[] {
  if (!Array.isArray(raw)) return [];
  const cards: SavedCard[] = [];
  for (const item of raw) {
    if (typeof item === 'string' && item.trim()) {
      const [pathSlug = '', lessonSlug = '', indexText = '0'] = item.split('/');
      cards.push({
        id: item,
        pathSlug,
        lessonSlug,
        index: Number(indexText) || 0,
        title: lessonSlug || item,
        lines: [],
        summary: indexText === 'summary',
        savedAt: '',
      });
      continue;
    }
    if (!item || typeof item !== 'object') continue;
    const record = item as Partial<SavedCard>;
    if (!record.id) continue;
    cards.push({
      id: String(record.id),
      pathSlug: String(record.pathSlug || ''),
      lessonSlug: String(record.lessonSlug || ''),
      index: Number(record.index) || 0,
      title: String(record.title || record.lessonSlug || record.id),
      lines: Array.isArray(record.lines)
        ? record.lines
            .filter((line) => line && typeof line.main === 'string')
            .slice(0, 2)
            .map((line) => ({ main: String(line.main), sub: String(line.sub || '') }))
        : [],
      summary: Boolean(record.summary),
      savedAt: String(record.savedAt || ''),
    });
  }
  return cards;
}

export function readDeck(): SavedCard[] {
  const store = storage();
  if (!store) return [];
  try {
    return normalizeSaves(JSON.parse(store.getItem(STORY_SAVE_KEY) || '[]'));
  } catch {
    return [];
  }
}

function writeDeck(cards: SavedCard[]): void {
  const store = storage();
  if (!store) return;
  store.setItem(STORY_SAVE_KEY, JSON.stringify(cards));
}

export function isSaved(id: string): boolean {
  return readDeck().some((card) => card.id === id);
}

/** Keep every guest card on the device and a copy under the new account id. */
export function claimStorySaves(userId: string): number {
  if (!userId) return 0;
  const store = storage();
  const device = readDeck();
  if (!store) return device.length;
  const key = `${STORY_SAVE_KEY}:${userId}`;
  let existing: SavedCard[] = [];
  try {
    existing = normalizeSaves(JSON.parse(store.getItem(key) || '[]'));
  } catch {
    existing = [];
  }
  const merged = mergeSavedCards(device, existing);
  store.setItem(key, JSON.stringify(merged));
  writeDeck(merged);
  return merged.length;
}

/** Returns whether the card is saved after the tap. */
export function toggleCard(card: SavedCard): boolean {
  const cards = readDeck();
  if (cards.some((item) => item.id === card.id)) {
    writeDeck(cards.filter((item) => item.id !== card.id));
    return false;
  }
  writeDeck([{ ...card, lines: card.lines.slice(0, 2), savedAt: card.savedAt || new Date().toISOString() }, ...cards]);
  return true;
}
